import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { Row, Col, message, Drawer, Switch, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { tasksApis } from "../../../api/NewTasks";

interface ChecklistQuestion {
  questionId: number;
  questionName: string;
  questionType: string;
  description?: string;
  isMandatory: boolean;
  optionsType?: Record<string, string>;
}

interface Checklist {
  checklistId: number;
  checklistName: string;
  checklistQuestions: ChecklistQuestion[];
  isUserDefined: number;
  updatedAt?: string;
  createdBy: number;
  updatedBy?: number;
  orgId?: number;
  createdByUserName?: string;
}

export interface RowData {
  id: string;
  status: string;
  checklist: number;
  mandatory: boolean;
  checklistRequired?: boolean;
}

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RowData[]) => void;
  setChecklistSubmissionStatus: (status: boolean) => void;
  ChecklistData: any[];
  siteId: number;
  associatedChecklistIds?: number[];
  associatedStatuses?: string[];
}

interface FormValues {
  [key: string]: any;
}

const CheckListDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setChecklistSubmissionStatus,
  ChecklistData,
  associatedChecklistIds,
  associatedStatuses,
}) => {
  const { handleSubmit, setValue } = useForm<FormValues>();

  const [rows, setRows] = useState<RowData[]>([
    { id: "1", status: "", checklist: 0, mandatory: false },
  ]);
  const [loading, setLoading] = useState(false);
  const [taskStatusList, setTaskStatusList] = useState<any[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [checklistOptions, setChecklistOptions] = useState<Checklist[]>([]);
  const [rowIdCounter, setRowIdCounter] = useState(2); // Start from 2 if your first row is "1"

  useEffect(() => {
    fetchTaskStatusList();
    fetchchcheckList();
  }, []);

  // Initialize rows with existing ChecklistData when drawer opens
  useEffect(() => {
    if (open && ChecklistData && ChecklistData.length > 0) {
      const initialRows = ChecklistData.map((item, index) => ({
        id: (index + 1).toString(),
        status: item.status || "",
        checklist: item.checklist || 0,
        mandatory: item.mandatory || false,
      }));
      setRows(initialRows);
      setRowIdCounter(initialRows.length + 1);
    } else if (open && (!ChecklistData || ChecklistData.length === 0)) {
      // Reset to default state when opening with no existing data
      setRows([{ id: "1", status: "", checklist: 0, mandatory: false }]);
      setRowIdCounter(2);
    }
  }, [open, ChecklistData]);

  const fetchTaskStatusList = async () => {
    try {
      setLoading(true);
      const response = await tasksApis.fetchTaskStatusList("Checklist");
      const { data, status } = response || {};
      if (status) {
        const formattedStatusList = data?.map((item: string) => ({
          label: item,
          key: item,
          value: item, // Use statusName as value for dropdown consistency
        }));
        setTaskStatusList(formattedStatusList);
      }
    } catch (error) {
      console.error("Error fetching task status list:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch task status list. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchchcheckList = async () => {
    try {
      setLoading(true);
      const response = await tasksApis.fetchTaskCheckList();
      const { data, status } = response || {};
      if (status) {
        setChecklistOptions(data);
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch checklist. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get available options for a specific row
  const getAvailableOptions = (currentRowId: string) => {
    const selectedStatuses = rows
      .filter((row) => row.id !== currentRowId && row.status)
      .map((row) => row.status);

    const selectedChecklists = rows
      .filter((row) => row.id !== currentRowId && row.checklist)
      .map((row) => row.checklist);

    // Exclude already associated statuses and checklists
    const availableStatuses = taskStatusList
      .map((item: any) => item.value)
      .filter(
        (status: string) =>
          !selectedStatuses.includes(status) &&
          !(associatedStatuses || []).includes(status)
      );

    const availableChecklists = checklistOptions.filter(
      (checklist) =>
        !selectedChecklists.includes(checklist.checklistId) &&
        !(associatedChecklistIds || []).includes(checklist.checklistId)
    );

    return { availableStatuses, availableChecklists };
  };

  // Add a new row after the given index
  const addRow = (afterIndex: number) => {
    // Get total available statuses (excluding already associated ones)
    const totalAvailableStatuses = taskStatusList
      .map((item: any) => item.value)
      .filter((status: string) => !(associatedStatuses || []).includes(status));

    // Check if we can add more rows
    if (rows.length >= totalAvailableStatuses.length) {
      messageApi.warning(
        `Cannot add more rows. Maximum ${totalAvailableStatuses.length} rows allowed based on available status options.`
      );
      return;
    }

    const newRowId = rowIdCounter.toString();
    setRowIdCounter((prev) => prev + 1);
    const newRow: RowData = {
      id: newRowId,
      status: "",
      checklist: 0,
      mandatory: false,
    };
    const newRows = [...rows];
    newRows.splice(afterIndex + 1, 0, newRow);
    setRows(newRows);
  };

  // Remove a row by id
  const removeRow = (rowId: string) => {
    setRows((prevRows) => {
      // If the row does not exist, do nothing (prevents double-click bug)
      if (!prevRows.some((row) => row.id === rowId)) {
        return prevRows;
      }
      // If only one row, show message and do nothing
      if (prevRows.length === 1) {
        messageApi.info("At least one row is required.");
        return prevRows;
      }
      // Remove the row
      const newRows = prevRows.filter((row) => row.id !== rowId);
      // If removal would result in 0 rows, block and show message
      if (newRows.length === 0) {
        messageApi.info("At least one row is required.");
        return prevRows;
      }
      return newRows;
    });
  };

  const updateRow = (rowId: string, field: keyof RowData, value: any) => {
    setRows(
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const submitApprovals: SubmitHandler<FormValues> = () => {
    // Validate that all rows have both status and checklist selected
    const invalidRows = rows.filter((row) => !row.status || !row.checklist);

    if (invalidRows.length > 0) {
      messageApi.open({
        type: "error",
        content:
          "Please select both Status and Checklist for all rows before submitting.",
      });
      return;
    }

    // Filter out any empty rows (rows with no status and no checklist)
    const validRows = rows.filter((row) => row.status && row.checklist);

    if (validRows.length === 0) {
      messageApi.open({
        type: "error",
        content:
          "Please add at least one checklist configuration before submitting.",
      });
      return;
    }

    onSubmit(validRows);
    setChecklistSubmissionStatus(true);
  };

  const handleStatusChange = (
    rowId: string,
    selectedOption: SingleValue<{ value: string; label: string }>
  ) => {
    if (selectedOption) {
      updateRow(rowId, "status", selectedOption.value);
      setValue(`status_${rowId}`, selectedOption.value);
    }
  };

  const handleChecklistChange = (
    rowId: string,
    selectedOption: SingleValue<{ value: number; label: string }>
  ) => {
    if (selectedOption) {
      updateRow(rowId, "checklist", selectedOption.value);
      setValue(`checklist_${rowId}`, selectedOption.value);
    }
  };

  const handleMandatoryChange = (rowId: string, checked: boolean) => {
    updateRow(rowId, "mandatory", checked);
    setValue(`mandatory_${rowId}`, checked);
  };

  return (
    <Drawer
      title="Set CheckList"
      placement="right"
      onClose={onClose}
      open={open}
      width={"50%"}
    >
      {contextHolder}
      <form onSubmit={handleSubmit(submitApprovals)}>
        {rows.map((row, index) => {
          const { availableStatuses, availableChecklists } =
            getAvailableOptions(row.id);
          return (
            <Row
              key={row.id}
              align="middle"
              style={{
                marginBottom: 24,
                background: "#fafbfc",
                borderRadius: 8,
                padding: 16,
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
              gutter={16}
            >
              <Col
                span={6}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <label style={{ fontWeight: 500, marginBottom: 4 }}>
                  Status
                </label>
                <div style={{ marginBottom: 0 }}>
                  <Select
                    classNamePrefix="react-select"
                    options={availableStatuses.map((status) => ({
                      value: status,
                      label: status,
                    }))}
                    onChange={(selectedOption) =>
                      handleStatusChange(row.id, selectedOption)
                    }
                    placeholder="Select Status"
                    value={
                      row.status
                        ? { value: row.status, label: row.status }
                        : null
                    }
                    styles={{
                      container: (base) => ({ ...base, minWidth: 120 }),
                    }}
                  />
                </div>
              </Col>
              <Col
                span={8}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <label style={{ fontWeight: 500, marginBottom: 4 }}>
                  Select Checklist
                </label>
                <div style={{ marginBottom: 0 }}>
                  <Select
                    classNamePrefix="react-select"
                    options={availableChecklists.map((checklist) => ({
                      value: checklist.checklistId,
                      label: checklist.checklistName,
                    }))}
                    onChange={(selectedOption) =>
                      handleChecklistChange(row.id, selectedOption)
                    }
                    placeholder="Select Checklist"
                    value={
                      row.checklist
                        ? {
                            value: row.checklist,
                            label:
                              checklistOptions.find(
                                (c) => c.checklistId === row.checklist
                              )?.checklistName || "",
                          }
                        : null
                    }
                    styles={{
                      container: (base) => ({ ...base, minWidth: 120 }),
                    }}
                  />
                </div>
              </Col>
              <Col
                span={6}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <label style={{ fontWeight: 500, marginBottom: 4 }}>
                  Mandatory
                </label>
                <div style={{ marginBottom: 0 }}>
                  <Select
                    classNamePrefix="react-select"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    onChange={(option) =>
                      handleMandatoryChange(row.id, option?.value ?? false)
                    }
                    placeholder="Mandatory"
                    value={
                      typeof row.mandatory === "boolean"
                        ? {
                            value: row.mandatory,
                            label: row.mandatory ? "Yes" : "No",
                          }
                        : null
                    }
                    styles={{
                      container: (base) => ({ ...base, minWidth: 120 }),
                    }}
                  />
                </div>
              </Col>
              <Col
                xs={24}
                sm={5}
                md={4}
                lg={4}
                xl={4}
                style={{
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 24,
                  }}
                >
                  {(() => {
                    const totalAvailableStatuses = taskStatusList
                      .map((item: any) => item.value)
                      .filter(
                        (status: string) =>
                          !(associatedStatuses || []).includes(status)
                      );
                    const canAddMore =
                      rows.length < totalAvailableStatuses.length;

                    if (rows.length === 1) {
                      return (
                        <>
                          <PlusOutlined
                            style={{
                              fontSize: 20,
                              color: canAddMore ? "#52c41a" : "#ccc",
                              cursor: canAddMore ? "pointer" : "not-allowed",
                            }}
                            onClick={
                              canAddMore ? () => addRow(index) : undefined
                            }
                          />
                          <MinusCircleOutlined
                            style={{
                              fontSize: 20,
                              color: rows.length === 1 ? "#ccc" : "#ff4d4f",
                              cursor:
                                rows.length === 1 ? "not-allowed" : "pointer",
                            }}
                            onClick={
                              rows.length === 1
                                ? undefined
                                : () => removeRow(row.id)
                            }
                          />
                        </>
                      );
                    } else if (index === rows.length - 1) {
                      return (
                        <>
                          <PlusOutlined
                            style={{
                              fontSize: 20,
                              color: canAddMore ? "#52c41a" : "#ccc",
                              cursor: canAddMore ? "pointer" : "not-allowed",
                            }}
                            onClick={
                              canAddMore ? () => addRow(index) : undefined
                            }
                          />
                          <MinusCircleOutlined
                            style={{
                              fontSize: 20,
                              color: rows.length === 1 ? "#ccc" : "#ff4d4f",
                              cursor:
                                rows.length === 1 ? "not-allowed" : "pointer",
                            }}
                            onClick={
                              rows.length === 1
                                ? undefined
                                : () => removeRow(row.id)
                            }
                          />
                        </>
                      );
                    } else {
                      return (
                        <MinusCircleOutlined
                          style={{
                            fontSize: 20,
                            color: rows.length === 1 ? "#ccc" : "#ff4d4f",
                            cursor:
                              rows.length === 1 ? "not-allowed" : "pointer",
                          }}
                          onClick={
                            rows.length === 1
                              ? undefined
                              : () => removeRow(row.id)
                          }
                        />
                      );
                    }
                  })()}
                </div>
              </Col>
            </Row>
          );
        })}
        <Row justify="end" style={{ marginTop: 32 }}>
          <Col>
            <Button type="primary" size="small" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      </form>
    </Drawer>
  );
};

export default React.memo(CheckListDrawer);
